import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { explainPushError, pushRoutesMissing } from './explainPushError'
import { enablePushOnThisDevice, pushSupported, roomPrefs, setRoomPref } from './pushOptIn'

/**
 * "Notify me about this room" — one checkbox, at the top of the Members sheet.
 *
 * Owner, 2026-09-18: 「把Notification做進去Project Chat。跟FriendChat這個理念,讓下拉
 * 選單,下拉後彈出的這個選單上面有一個Checkbox可以允許這個Project Chat或是Friend Chat
 * 上面是否要輸送Notification…每個使用者都有自己的權利去選擇是否要被這個Chatroom
 * Notify或是不要Notify」
 *
 * ONE COMPONENT SERVES BOTH KINDS OF ROOM, because a friend DM already IS a
 * ProjectPanel: useManagedFriendDm hands the managed conversation back as a
 * ProjectRow, and the same Members sheet opens from the same title button. So
 * "Project Chat 跟 Friend Chat" needed one checkbox, not two — and the room's id
 * is the only thing either case has to say.
 *
 * EVERY PERSON'S OWN CHOICE, which is why the preference is stored against the
 * READER and not against the conversation. A flag on the room would be whoever
 * ticked it last deciding for everyone in it. The consequence to notice while
 * reading this: what the checkbox shows is this account's answer, and two members
 * of the same room will correctly see it in different positions.
 *
 * ABSENT WHERE IT CANNOT WORK, exactly like the agent switch — a browser and the
 * 1.0 store build have no push plugin, and a control that invites a tap and then
 * explains nothing is worse than no control.
 */
export function RoomPushToggle({ convId }: { convId: string }) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const prefs = useQuery({
    queryKey: ['room-push-prefs'],
    queryFn: roomPrefs,
    enabled: pushSupported(),
  })

  if (!pushSupported()) return null
  /*
   * A SERVER WITHOUT THESE ROUTES SHOWS NO SWITCH, rather than a switch that
   * explains itself. Owner, 2026-09-18: he ticked this on app-dev while the
   * BFF image was still building and was told notifications were unavailable.
   * The sentence was true and the control should not have been there to read
   * it. Costs nothing — the read already ran when the sheet opened.
   */
  if (pushRoutesMissing(prefs.error)) return null

  const on = (prefs.data ?? []).includes(convId)

  const toggle = async () => {
    if (busy) return
    setBusy(true)
    setNote(null)
    try {
      if (!on) {
        /*
         * PERMISSION FIRST, AND ONLY WHEN SWITCHING ON — the same rule as the
         * agent switch and for the same reason: iOS grants once, and a
         * "Don't Allow" collected from someone who never asked for
         * notifications cannot be taken back from inside the app.
         *
         * Asked again here rather than assumed from the agent switch, because
         * a person may well arrive at a room having never opened an agent's
         * sheet. When permission was already granted this resolves immediately
         * and shows nothing.
         */
        const perm = await enablePushOnThisDevice()
        if (perm === 'denied') {
          setNote(
            'Notifications are off for Avengents in iOS Settings — turn them on there first.',
          )
          return
        }
        if (perm !== 'granted') {
          setNote('iOS did not grant notifications. Try again.')
          return
        }
      }
      await setRoomPref(convId, !on)
      await qc.invalidateQueries({ queryKey: ['room-push-prefs'] })
    } catch (e) {
      setNote(explainPushError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="pr-push">
      <label className="ac-push">
        <input
          type="checkbox"
          checked={on}
          disabled={busy || prefs.isLoading}
          onChange={() => void toggle()}
          aria-label="Notify me about new messages here"
        />
        <span>Notify me about new messages here</span>
      </label>
      <p className="ac-push-hint">
        {on
          ? 'Sent to every phone signed into this account.'
          : 'Off — new messages here will not notify you.'}
      </p>
      {(note ?? (prefs.error ? explainPushError(prefs.error) : null)) && (
        <p className="err-line sm-err">{note ?? explainPushError(prefs.error)}</p>
      )}
    </div>
  )
}
